import { type ReactNode } from 'react'
import {
  CertificateProgramEngagementActionType,
  CertificateProgramEngagementCta,
} from '@mc/api'
import { useBreakpoint } from '@mc/media'
import { paths } from '@mc/paths'
import { useStaticData } from '@mc/static-data-react'
import { ProductLine } from '@mc/track-event'
import {
  CertificatesMobileMenu,
  GlobalTopNavigation,
} from 'components/Navigation/global-navigation'
import { TrackingAttributes } from 'pages/certificates/tracking'
import {
  type CertificateInfo,
  type LatestCertificateInfo,
} from '../../../common'
import { SignUpButton } from '../../SignUpButton'
import { LoginLink } from './LoginLink'
import { BaseNavigation, NavigationMenu, signOutHandler } from '..'

export interface PreEnrolledNavigationProps {
  topics: CertificateInfo[]
  latestCertificates: LatestCertificateInfo[]
  currentCertificateTitle?: string
  currentCertificateImg?: { url?: string; altText?: string }
  currentCertificateSignupEnabled?: boolean
  navigationActions?: CertificateProgramEngagementCta[]
  redirectTo?: string
  showTopNavigation?: boolean
  aapUser?: boolean
  rightContentClassName?: string
  className?: string
  // Replaces the default sign-up button; still gated by the PURCHASE_CERTIFICATE
  // navigation action.
  signUpButton?: ReactNode
}

export const PreEnrolledNavigation = ({
  topics: shuffledCertificates,
  latestCertificates,
  currentCertificateTitle,
  currentCertificateImg,
  currentCertificateSignupEnabled,
  navigationActions,
  redirectTo,
  aapUser,
  rightContentClassName,
  className = '',
  signUpButton = <SignUpButton className='hidden md:inline' />,
}: PreEnrolledNavigationProps) => {
  const getLogoHref = () => redirectTo ?? paths.certificates

  // TODO: We need to follow up with a change where we actually render the navigation actions
  // and not just the sign up button, but for now we'll just render the sign up button if it exists
  // to minimize the impact of this change.
  const navSignUpButton = navigationActions?.find(
    (action) =>
      action.actionType ===
      CertificateProgramEngagementActionType.PURCHASE_CERTIFICATE,
  )
    ? signUpButton
    : null

  const { isLoggedIn } = useStaticData<{ isLoggedIn: boolean }>()
  const { isSm } = useBreakpoint()

  if (isLoggedIn && !aapUser) {
    // No experiment for logged in users with no AAP
    return (
      <BaseNavigation
        logoHref={getLogoHref()}
        preEnrolled
        rightContent={
          <div className='flex items-center gap-4'>
            {navSignUpButton}
            <NavigationMenu onSignOut={signOutHandler} />
          </div>
        }
        trackingData={{ action: 'MC Logo' }}
        className={className}
      />
    )
  }

  return (
    <TrackingAttributes location='nav bar'>
      {isLoggedIn && aapUser ? (
        // When the user is logged in and has AAP, we show the "post-paywall" navigation experiment.
        <>
          {isSm ? (
            <LoggedInMobileNavigation
              getLogoHref={getLogoHref}
              signUpButton={navSignUpButton}
              className={className}
            />
          ) : (
            <LoggedInDesktopNavigation
              getLogoHref={getLogoHref}
              signUpButton={navSignUpButton}
              className={className}
            />
          )}
        </>
      ) : (
        // When the user is logged out or does not have AAP, we show the "pre-paywall" navigation experiment.
        <LoggedOutDesktopNavigation
          getLogoHref={getLogoHref}
          signUpButton={navSignUpButton}
          shuffledCertificates={shuffledCertificates}
          latestCertificates={latestCertificates}
          currentCertificateTitle={currentCertificateTitle}
          currentCertificateImg={currentCertificateImg}
          currentCertificateSignupEnabled={!!currentCertificateSignupEnabled}
          rightContentClassName={rightContentClassName}
          className={className}
        />
      )}
    </TrackingAttributes>
  )
}

const LoggedInMobileNavigation = ({
  getLogoHref,
  signUpButton,
  className,
}: {
  getLogoHref: () => string
  signUpButton: ReactNode
  className?: string
}) => (
  <BaseNavigation
    logoHref={getLogoHref()}
    preEnrolled
    rightContent={
      <div className='flex items-center gap-4'>
        {signUpButton}
        <NavigationMenu onSignOut={signOutHandler} />
      </div>
    }
    trackingData={{ action: 'MC Logo' }}
    className={className}
  />
)

const LoggedInDesktopNavigation = ({
  getLogoHref,
  signUpButton,
  className,
}: {
  getLogoHref: () => string
  signUpButton: ReactNode
  className?: string
}) => (
  <BaseNavigation
    logoHref={getLogoHref()}
    preEnrolled
    rightContent={
      <div className='flex items-center gap-4'>
        {signUpButton}
        <NavigationMenu onSignOut={signOutHandler} />
      </div>
    }
    trackingData={{ action: 'MC Logo' }}
    className={className}
  />
)

const LoggedOutDesktopNavigation = ({
  getLogoHref,
  signUpButton,
  shuffledCertificates,
  latestCertificates,
  currentCertificateTitle,
  currentCertificateImg,
  currentCertificateSignupEnabled,
  rightContentClassName,
  className,
}: {
  getLogoHref: () => string
  signUpButton: ReactNode
  shuffledCertificates: CertificateInfo[]
  latestCertificates: LatestCertificateInfo[]
  currentCertificateTitle: string | undefined
  currentCertificateImg: { url?: string; altText?: string } | undefined
  currentCertificateSignupEnabled: boolean
  rightContentClassName?: string
  className?: string
}) => (
  <>
    <GlobalTopNavigation
      className='py-1'
      showOnCallLink
      showAtWorkLink
      product={ProductLine.CERTIFICATES}
    />
    <BaseNavigation
      logoHref={getLogoHref()}
      preEnrolled
      rightContent={
        <div className='flex items-center gap-4'>
          {signUpButton}
          <LoginLink
            className={`hidden lg:inline c-body-bold ${rightContentClassName || ''}`}
          />
          <CertificatesMobileMenu
            certificates={shuffledCertificates.slice(0, 6)}
            latestCertificates={latestCertificates}
            currentCertificateTitle={currentCertificateTitle}
            currentCertificateImg={currentCertificateImg}
            signupEnabled={currentCertificateSignupEnabled}
          />
        </div>
      }
      trackingData={{ action: 'MC Logo' }}
      className={className}
    />
  </>
)
